import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";
import { useDebounce } from "@/hooks/useDebounce";
import { useNavigate } from "react-router-dom";

import { setNotificationState } from "@/store/slice/notificationSlice";

import SearchSvg from "@/assets/svg/SearchSvg";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { RequireAuth } from "@/components/RequireAuth";
import NotificationSvg from "@/assets/svg/NotificationSvg";

import { useAppSelector } from "@/hooks/useAppSelector";
import { useGetUniversalSearchQuery } from "@/store/api/userApi";
import theme from "@/theme";
import { styles } from "./styleSearchCompanies";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setUnreadCount } from "@/store/slice/notificationSlice";

import { getToken } from "@/helpers/cookiesHelper";
import { HIGHLIGHTED_TICKERS, getTypeBg } from "@/helpers/tickerHelpers";
import { useNotifications } from "@/hooks/useNotifications";
import { useLazyGetNotificationsQuery } from "@/store/api/notificationApi";

interface SearchCompany {
  data: {
    name: string;
    code: string;
    type: string;
  };
}

const SearchCompanies: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { unreadCount, awayBadge, isOpen: isNotificationOpen } = useAppSelector(
    (state) => state.notificationState
  );

  const badgeCount = unreadCount + (awayBadge ? 1 : 0);

  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchCompany[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("");

  const debounced = useDebounce(value, 300);

  const { data: searchCompanies } = useGetUniversalSearchQuery(
    { limit: 40, query: debounced, type: "instruments" },
    { skip: !debounced }
  );

  const anchorRef = useRef<HTMLInputElement | null>(null);

  const open = searchFocused && !!anchorRef.current && value.trim() !== "";
  const canBeOpen = open && Boolean(anchorRef);
  const id = canBeOpen ? "transition-popper" : undefined;
  const token = getToken();
  const { last, feed } = useNotifications(token as string);
  const [lazyNotification] = useLazyGetNotificationsQuery();

  useEffect(() => {
    async function fetchUnread() {
      const result = await lazyNotification({}).unwrap();
      const unread = result.data?.filter((item: any) => !item.read).length || 0;

      dispatch(setUnreadCount(unread));
    }
    fetchUnread();
  }, [token, lazyNotification, feed, last]);

  useEffect(() => {
    if (debounced.trim() === "") {
      setSearchResults([]);
      setTotalResults(0);
      return;
    }
    if (searchCompanies?.results) {
      setSearchResults(searchCompanies.results);
      setTotalResults(searchCompanies.total ?? 0);
    }
  }, [debounced, searchCompanies]);

  useEffect(() => {
    if (value.trim() === "") {
      setSearchResults([]);
      setTotalResults(0);
      setLoading(false);
      return;
    }
    setLoading(true);
  }, [value]);

  useEffect(() => {
    if (searchCompanies?.results) {
      setSearchResults(searchCompanies.results);
      setTotalResults(searchCompanies.total ?? 0);
    }
    setLoading(false);
  }, [searchCompanies]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box sx={styles.rightBlock}>
      <Box position={"relative"} sx={{ margin: "8px 16px 4px 0px" }}>
        <TextField
          autoComplete="off"
          placeholder="Search companies"
          variant="outlined"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          inputRef={anchorRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-describedby={id}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchSvg color={searchFocused ? theme.palette.custom.mainBlue : "#9DA6B0"} />
              </InputAdornment>
            ),
            sx: styles.searchFieldInput,
          }}
          sx={styles.searchField(searchFocused)}
        />
        {open && (
          <Box sx={styles.searchResultsWrapper} className="custom-scrollbar">
            {loading ? (
              <Box
                sx={{
                  height: "325px",
                  alignItems: "center",
                  justifyContent: "center",
                  display: open ? "flex" : "none",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <CustomLoader size={24} />
              </Box>
            ) : Number(totalResults) > 0 ? (
              <List disablePadding>
                {searchResults.map((item: SearchCompany, index: number) => (
                  <ListItemButton
                    key={index}
                    sx={styles.searchResultItem(getTypeBg(item.data.type))}
                    onMouseDown={() => {
                      navigate(`/stock/${item.data.code}`);
                      setSearchFocused(false);
                    }}
                  >
                    <Typography sx={styles.searchResultPrimaryText}>{item.data.name}</Typography>
                    <Typography
                      sx={
                        HIGHLIGHTED_TICKERS.has(item.data.code)
                          ? styles.searchResultSecondaryTextHighlighted
                          : styles.searchResultSecondaryText
                      }
                    >
                      {item.data.code} | {item.data.type}
                    </Typography>
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  height: "325px",
                  padding: "12px 8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography sx={styles.searchResultPrimaryText}>Sorry, nothing found.</Typography>
                <Typography sx={styles.searchResultSecondaryText}>Try another search.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <RequireAuth>
        <Badge
          badgeContent={badgeCount}
          color="primary"
          overlap="circular"
          invisible={badgeCount === 0}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiBadge-badge": {
              maxWidth: "22px",
              width: badgeCount > 9 ? "22px" : "16px",
              minWidth: "16px",
              height: "16px",
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "9px",
              lineHeight: 0,
              backgroundColor: theme.palette.custom.mainBlue,
              color: "#FFFFFF",
              borderRadius: "18px",
              border: "2px solid white",
              padding: 0,
            },
          }}
        >
          <IconButton
            onClick={() => dispatch(setNotificationState(!isNotificationOpen))}
            sx={styles.notificationButton}
          >
            <NotificationSvg height={24} width={24} />
          </IconButton>
        </Badge>
      </RequireAuth>
    </Box>
  );
};

export default SearchCompanies;
